/**
 * Numbered business documents are final once they leave 'draft'.
 *
 * A quote (ה), a bill (ד) or a supplier bid request (ב) that is out of draft has been printed and
 * posted: we keep tracking it, we never change it. Its PDF lives at a deterministic Storage path,
 * so any write to the document would re-render over the exact file the recipient already holds.
 * To change anything, issue a new document with a new number.
 *
 * What stays writable is only the tracking ledger that legitimately accrues afterwards — payments
 * against a bill, supplier responses to a bid request, the status the document moves through.
 *
 * Mirrored server-side in firestore.rules (`lockedDocWriteOk`), which is the actual enforcement;
 * these guards exist so the UI fails loudly instead of hitting a rules error.
 */

export const LOCKED_DOC_WRITABLE_FIELDS = {
  quote: ['status', 'sentAt', 'acceptedAt', 'rejectedAt', 'updatedAt', 'updatedBy'],
  bill: [
    'status',
    'issuedAt',
    'cancelledAt',
    'payments',
    'amountPaid',
    'balance',
    'updatedAt',
    'updatedBy',
  ],
  bidRequest: [
    'status',
    'supplierResponses',
    'supplierLinks',
    'selectedSupplierId',
    'convertedToOrderId',
    'convertedOrderId',
    'convertedOrderNumber',
    'convertedSupplierName',
    'convertedBy',
    'emailStatus',
    'emailSentAt',
    'emailSentBy',
    'emailFailedDetails',
    'respondedAt',
    'updatedAt',
    'updatedBy',
  ],
} as const satisfies Record<string, readonly string[]>;

export type NumberedDocKind = keyof typeof LOCKED_DOC_WRITABLE_FIELDS;

const DOC_LABELS: Record<NumberedDocKind, string> = {
  quote: 'ההצעה נשלחה',
  bill: 'חשבון העסקה הונפק',
  bidRequest: 'בקשת ההצעה נשלחה',
};

/** Every numbered document uses the same signal: anything past 'draft' is out in the world. */
export const isDocLocked = (status: string | undefined): boolean =>
  status !== undefined && status !== 'draft';

/** Throws if `data` would change anything but the tracking ledger of a locked document. */
export function assertDocWritable(
  kind: NumberedDocKind,
  status: string | undefined,
  data: Record<string, unknown>
): void {
  if (!isDocLocked(status)) return;
  const allowed: readonly string[] = LOCKED_DOC_WRITABLE_FIELDS[kind];
  const blocked = Object.keys(data).filter((key) => !allowed.includes(key));
  if (blocked.length > 0) {
    throw new Error(`${DOC_LABELS[kind]} ואינו ניתן לשינוי (${blocked.join(', ')})`);
  }
}
